/**
 * A2A Client Configuration for EnglishWritingTools
 * 
 * Initializes the A2A client for communicating with the A2AEnglishAI backend server.
 */

import { A2AClient } from '../shared/a2a-client.js';

// A2A Backend Server URLs
const LOCALHOST_URL = 'http://localhost:8787';
const PRODUCTION_URL = 'https://a2aenglishai-server.appsokay.net';

// Get initial URL from localStorage or environment variable
// Default to PRODUCTION (Cloudflare) for deployed apps
const getInitialUrl = () => {
  const isProduction = typeof window !== 'undefined' && 
                       window.location.hostname !== 'localhost' && 
                       window.location.hostname !== '127.0.0.1' &&
                       !window.location.hostname.includes('localhost');
  
  if (isProduction) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('a2a_use_localhost', 'false');
    }
    return PRODUCTION_URL;
  }
  
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('a2a_use_localhost');
    if (saved === 'true') {
      return LOCALHOST_URL;
    }
    return PRODUCTION_URL;
  }
  
  return process.env.REACT_APP_A2A_BACKEND_URL || PRODUCTION_URL;
};

const REMOTE_AGENT_URL = getInitialUrl();
const AGENT_ID = 'englishwritingtools-client-001';
const SECRET_KEY = process.env.REACT_APP_A2A_SECRET_KEY || null;

// Always log (even in production) for debugging
console.log('[A2A Client Config]', {
  backendURL: REMOTE_AGENT_URL,
  agentID: AGENT_ID,
  hasSecretKey: !!SECRET_KEY,
  isLocalhost: REMOTE_AGENT_URL.includes('localhost'),
  note: 'Use the A2A Server toggle (bottom left) to switch between localhost and production'
});

const englishWritingClient = new A2AClient(REMOTE_AGENT_URL, AGENT_ID, {
  secretKey: SECRET_KEY,
  timeout: 30000,
  maxRetries: 3,
  enableLogging: process.env.NODE_ENV === 'development',
});

let capabilitiesInitialized = false;
let initializationPromise = null;

export const updateA2AServerUrl = (newUrl) => {
  if (initializationPromise) {
    initializationPromise = null;
  }
  englishWritingClient.updateRemoteUrl(newUrl);
  englishWritingClient.clearCache();
  capabilitiesInitialized = false;
  console.log(`[A2A Client] Server URL updated to: ${newUrl}`);
  if (englishWritingClient.remoteUrl === newUrl) {
    initializeCapabilities().catch(error => {
      const isConnectionError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NETWORK_ERROR')
      );
      if (!isConnectionError) {
        console.warn('[A2A Client] Failed to reinitialize with new URL:', error.message);
      }
    });
  }
};

if (typeof window !== 'undefined') {
  window.updateA2AServerUrl = updateA2AServerUrl;
}

export const initializeCapabilities = async () => {
  if (capabilitiesInitialized) {
    return englishWritingClient.getCapabilities();
  }
  if (initializationPromise) {
    return initializationPromise;
  }
  initializationPromise = (async () => {
    try {
      console.log('[A2A Client] Discovering capabilities from backend...');
      const capabilities = await englishWritingClient.discoverCapabilities();
      capabilitiesInitialized = true;
      console.log(`[A2A Client] ✓ Capability discovery successful: Found ${capabilities.length} capabilities from ${englishWritingClient.remoteUrl}`);
      return capabilities;
    } catch (error) {
      const isConnectionError = error.message && (
        error.message.includes('Failed to fetch') ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('NETWORK_ERROR')
      );
      
      if (!isConnectionError) {
        console.error('[A2A Client] Failed to discover capabilities:', error);
      }
      throw error;
    }
  })();
  return initializationPromise;
};

if (typeof window !== 'undefined') {
  // Always log initialization attempt (even in production) for debugging
  console.log('[A2A Client] Auto-initialization scheduled', {
    remoteUrl: englishWritingClient.remoteUrl,
    agentId: AGENT_ID,
    isLocalhost: REMOTE_AGENT_URL.includes('localhost')
  });
  
  setTimeout(() => {
    const currentUrl = englishWritingClient.remoteUrl;
    const expectedUrl = getInitialUrl();
    if (currentUrl === expectedUrl) {
      console.log('[A2A Client] Starting capability discovery...');
      initializeCapabilities().then(capabilities => {
        console.log(`[A2A Client] ✓ Capability discovery successful: Found ${capabilities?.length || 0} capabilities`);
      }).catch(error => {
        const isConnectionError = error.message && (
          error.message.includes('Failed to fetch') ||
          error.message.includes('ERR_CONNECTION_REFUSED') ||
          error.message.includes('NETWORK_ERROR')
        );
        // Always log errors (even in production) for debugging
        console.error('[A2A Client] Auto-initialization failed', {
          error: error.message,
          isConnectionError,
          url: currentUrl
        });
      });
    } else {
      console.log(`[A2A Client] Skipping auto-initialization - URL was updated by toggle (${currentUrl} vs ${expectedUrl})`);
    }
  }, 2000);
}

export const checkBackendAvailability = async () => {
  try {
    const capabilities = await englishWritingClient.discoverCapabilities();
    return capabilities && capabilities.length > 0;
  } catch (error) {
    console.error('[A2A Client] Backend not available:', error);
    return false;
  }
};

export const getCapabilityInfo = async (capabilityName) => {
  return await englishWritingClient.getCapability(capabilityName);
};

export const hasCapability = async (capabilityName) => {
  return await englishWritingClient.hasCapability(capabilityName);
};

export const getCapabilities = () => {
  return englishWritingClient.getCapabilities();
};

export const executeTask = async (capabilityName, parameters = {}, options = {}) => {
  return await englishWritingClient.executeTask(capabilityName, parameters, options);
};

export const clearCache = () => {
  englishWritingClient.clearCache();
  capabilitiesInitialized = false;
  initializationPromise = null;
};

export const cleanup = () => {
  englishWritingClient.cleanup();
};

export default englishWritingClient;

